import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import blogApi from '@/api/blog';
import SEO from '@/components/ui/SEO';
import Loading from '@/components/ui/Loading';
import NotFoundPage from './NotFoundPage';
import BlogPostCard from '@/components/blog/BlogPostCard';
import BlogPagination from '@/components/blog/BlogPagination';
import { useLanguage } from '@/context/LanguageContext';

const BlogAuthorPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10);

  const { data, isLoading, error } = useQuery({
    queryKey: ['blog', 'author', slug, { page, lang: language }],
    queryFn: () => blogApi.getAuthorWithPosts(slug!, { page, pageSize: 12, lang: language }),
    enabled: !!slug,
  });

  if (isLoading) return <Loading fullScreen />;
  if (error || !data) return <NotFoundPage />;

  const { author, posts, totalCount, pageSize } = data;

  return (
    <>
      <SEO
        title={`${author.displayName} - Blog`}
        description={author.bio || `Articles by ${author.displayName}`}
      />

      {/* Author Header */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
          {author.avatarUrl && (
            <img
              src={author.avatarUrl}
              alt={author.displayName}
              className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
            />
          )}
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{author.displayName}</h1>
          {author.bio && (
            <p className="text-gray-600 max-w-xl mx-auto mb-4">{author.bio}</p>
          )}

          {/* Social Links */}
          <div className="flex justify-center gap-4">
            {author.websiteUrl && (
              <a href={author.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition-colors" aria-label="Website">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" /></svg>
              </a>
            )}
            {author.twitterUrl && (
              <a href={author.twitterUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition-colors" aria-label="Twitter">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            )}
            {author.linkedInUrl && (
              <a href={author.linkedInUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition-colors" aria-label="LinkedIn">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            )}
            {author.gitHubUrl && (
              <a href={author.gitHubUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition-colors" aria-label="GitHub">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="section">
        <div className="container mx-auto px-6 lg:px-12">
          {posts.length === 0 ? (
            <div className="text-center py-12 text-gray-600">
              <p>No posts by this author yet.</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </div>

              <BlogPagination page={page} pageSize={pageSize} totalCount={totalCount} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogAuthorPage;
